import {
  HiCalendarDays,
  HiDocumentText,
  HiCheck,
  HiXMark,
  HiClock,
  HiArrowDownTray,
  HiArrowUpTray,
  HiTrash,
  HiOutlineEyeSlash,
  HiPrinter,
  HiChevronDown,
  HiChevronUp,
  HiPaperClip,
} from 'react-icons/hi2'
import { FaRegBuilding } from 'react-icons/fa'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '../../../../components/Cards'
import {
  RichTextDisplay,
  Badge,
  Input,
  Button,
  HistoryTimeline,
  DemandToolbar,
  Textarea,
  AlertModal,
  ConfirmModal,
  PromptModal,
  SimpleButton,
} from '../../../../components'
import type { Demanda, ObservadorData } from '../../../../models'
import type { Observer } from '../modals/ManageObserversModal'
import { useDemandPermissions } from '../../../../hooks/useDemandPermissions'
import { formatDateSimple, getFileIcon, formatDateForDisplay, formatDateForInput } from '@/utils'
import { useDemandDetails } from '../../hooks/useDemandDetails'
import { useDemandViewHelpers } from '../../hooks/useDemandViewHelpers'
import { useDemandAutorizacao } from '../../hooks/useDemandAutorizacao'
import { useDemandAutorizacoes } from '../../hooks/queries/useAutorizacoesQuery'
import { AutorizacaoTimeline } from '../../../../components/AutorizacaoTimeline'
import { InfoDemands } from './components/Infodemands'
import { TemplateFieldsViewer } from '@/views/templates'
import {
  AssignDemandModal,
  SolveDemandModal,
  FinalizeDemandModal,
  AttachFilesModal,
  NavigationUnsavedChangesModal,
  ManageObserversModal,
  AutorizacaoModal,
  RemoveAssignedModal,
} from '../modals'
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getPriorityColor,
  getPriorityIcon,
} from '../../utils/demandUtils'
import { useKeyboardShortcut } from '../../../../hooks/useKeyboardShortcut'
import { useKeyboardShortcutsContext } from '../../../../contexts/KeyboardShortcutsContext'
import { usePrintHistory } from '../../../../hooks/usePrintHistory'

/**
 * Converte observadores da demanda para o formato esperado pelo ManageObserversModal
 */
const mapObserversForModal = (observadores?: ObservadorData[]): Observer[] => {
  if (!observadores) return []

  return observadores.map(obs => {
    // Formato: Observador direto
    if ('observador_fk' in obs) {
      const mapped: Observer = {
        id: obs.id || 0,
        observador_fk: obs.observador_fk,
        active: obs.active,
        ...(obs.observador && {
          usuario: {
            name: obs.observador.name,
            email: obs.observador.email,
          },
        }),
      }
      return mapped
    }

    // Formato: User com pivot
    if ('pivot' in obs && obs.pivot) {
      const mapped: Observer = {
        id: obs.id,
        observador_fk: obs.pivot.observador_fk,
        active: obs.pivot.active ?? true,
        usuario: {
          name: obs.name,
          email: obs.email,
        },
      }
      return mapped
    }

    // Fallback (não deveria chegar aqui)
    const mapped: Observer = {
      id: obs.id || 0,
      observador_fk: 0,
      active: true,
    }
    return mapped
  })
}

/**
 * Converte atribuídos da demanda para o formato esperado pelo RemoveAssignedModal
 */
const mapAssignedForModal = (atribuidas?: any[]) => {
  if (!atribuidas) return []

  return atribuidas
    .filter(a => a.active)
    .map(a => ({
      id: a.id || a.usuario_fk, // Usar usuario_fk como fallback se id não existir
      usuario_fk: a.usuario_fk,
      active: a.active,
      status: a.status || 'EMPROGRESSO',
      usuario: a.user ||
        a.usuario || {
          id: a.usuario_fk,
          name: 'Usuário Desconhecido',
          email: '',
        },
    }))
}

// Extensão local da interface para incluir demandados
interface DemandaExtended extends Demanda {
  demandados?: any[]
}

interface DemandDetailsProps {
  demanda: DemandaExtended
  isEditing?: boolean
  onEditToggle?: () => void
  onSave?: (updatedDemanda: Partial<DemandaExtended>) => void
}

export const DemandDetails = ({
  demanda,
  isEditing = false,
  onEditToggle,
  onSave,
}: DemandDetailsProps) => {
  const navigate = useNavigate()
  const { enabled: shortcutsEnabled } = useKeyboardShortcutsContext()
  const [toolbarWaitingForAction, setToolbarWaitingForAction] = useState(false)
  const [isAttachmentsExpanded, setIsAttachmentsExpanded] = useState(false)
  const [isResendFlow, setIsResendFlow] = useState(false)
  const [resendComment, setResendComment] = useState('')
  const { printHistory } = usePrintHistory()

  // Use the main hook that combines all functionality
  const demandData = useDemandDetails({
    demanda,
    isEditing,
    onEditToggle: onEditToggle || (() => {}),
    onSave: onSave || (() => {}),
  })

  // Hook de permissões baseado nas regras do backend
  const permissions = useDemandPermissions(demanda, demandData.currentUser)

  // Hook para helpers de visualização
  const viewHelpers = useDemandViewHelpers({
    demanda,
    currentUser: demandData.currentUser,
    setores: demandData.setores,
  })

  // Hook para autorização de demandas
  const autorizacao = useDemandAutorizacao({
    demanda,
    currentUser: demandData.currentUser,
  })

  // Hook para buscar todas as autorizações da demanda
  const { data: autorizacoesData } = useDemandAutorizacoes(demanda.id!)

  // Ação para criar demanda relacionada: navega para criação com ID da demanda atual
  const handleRelatedDemand = () => {
    if (demanda.id) {
      // Navegar para a página de criação de demanda passando o ID da demanda atual como relacionada
      navigate(`/demands/new?relacionada_fk=${demanda.id}`)
    } else {
      // Fallback: navegar sem relacionamento se não houver ID
      navigate('/demands/new')
    }
  }

  const handlePrintHistory = () => {
    const historyPayload = {
      items: demandData.historico,
      demandaTitulo: demanda.titulo,
      demandaId: demanda.id || 0,
      status: getStatusLabel(demanda.status || ''),
      prioridade: demanda.prioridade,
    }

    printHistory(historyPayload)
  }

  const handleStartEdit = () => {
    setIsResendFlow(false)
    if (onEditToggle) {
      onEditToggle()
    }
  }

  const handleStartResend = () => {
    setIsResendFlow(true)
    if (onEditToggle && !isEditing) {
      onEditToggle()
    }
  }

  const handleCancelResend = () => {
    setIsResendFlow(false)
    setResendComment('')
  }

  const handleCancelResendAndEdit = () => {
    setIsResendFlow(false)
    setResendComment('')
    // Forçar saída do modo de edição sem confirmação
    if (onEditToggle && isEditing) {
      // Resetar dados de edição
      demandData.setEditData({
        descricao: demanda.descricao || '',
        sugestao_entrega: demanda.sugestao_entrega || '',
        template_respostas: demanda.template_respostas || undefined
      })
      onEditToggle()
    }
  }

  const handleResendDemand = async () => {
    const success = await demandData.handleRemandDemand({
      descricao: demandData.editData.descricao,
      sugestao_entrega: demandData.editData.sugestao_entrega,
      template_respostas: demandData.editData.template_respostas,
      comentario: resendComment.trim() || undefined,
    })
    if (success) {
      setIsResendFlow(false)
      setResendComment('')
      // Sair do modo de edição após reenvio bem-sucedido
      if (onEditToggle && isEditing) {
        onEditToggle()
      }
    }
  }

  // Keyboard shortcut: P - Toggle info panel
  useKeyboardShortcut(
    'p',
    () => {
      // Simular clique no botão de expandir/colapsar
      const button = document.querySelector('[data-info-toggle]') as HTMLButtonElement
      if (button) {
        button.click()
      }
    },
    {
      enabled: shortcutsEnabled,
      description: 'Abrir/Fechar Informações',
      category: 'demand-view',
    }
  )

  // Keyboard shortcut: Ctrl+B - Activate toolbar mode
  useKeyboardShortcut(
    'ctrl+b',
    () => {
      setToolbarWaitingForAction(true)
      // Reset after 2 seconds
      setTimeout(() => setToolbarWaitingForAction(false), 2000)
    },
    {
      enabled: shortcutsEnabled,
      description: 'Ativar modo toolbar',
      category: 'demand-view',
    }
  )

  // Toolbar action shortcuts (only active after Ctrl+B)
  // P - Pull to self
  useKeyboardShortcut(
    'p',
    () => {
      if (toolbarWaitingForAction && permissions.canPull && demandData.handlePullToSelf) {
        demandData.handlePullToSelf()
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Puxar para mim',
      category: 'toolbar',
    }
  )

  // E - Edit
  useKeyboardShortcut(
    'e',
    () => {
      if (
        toolbarWaitingForAction &&
        !viewHelpers.isOnlyObserver &&
        permissions.canEdit &&
        onEditToggle
      ) {
        handleStartEdit()
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Editar demanda',
      category: 'toolbar',
    }
  )

  // A - Assign
  useKeyboardShortcut(
    'a',
    () => {
      if (toolbarWaitingForAction && !viewHelpers.isOnlyObserver && permissions.canAssign) {
        demandData.setAssignModal({ ...demandData.assignModal, isOpen: true })
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Atribuir demanda',
      category: 'toolbar',
    }
  )

  // S - Solve
  useKeyboardShortcut(
    's',
    () => {
      if (toolbarWaitingForAction && !viewHelpers.isOnlyObserver && permissions.canSolve) {
        demandData.setSolveModal({ ...demandData.solveModal, isOpen: true })
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Solucionar demanda',
      category: 'toolbar',
    }
  )

  // C - Accept
  useKeyboardShortcut(
    'c',
    () => {
      if (
        toolbarWaitingForAction &&
        !viewHelpers.isOnlyObserver &&
        permissions.canAccept &&
        demandData.handleAccept
      ) {
        demandData.handleAccept()
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Aceitar demanda',
      category: 'toolbar',
    }
  )

  // M - Comment
  useKeyboardShortcut(
    'm',
    () => {
      if (toolbarWaitingForAction && permissions.canComment && demandData.handleComment) {
        demandData.handleComment()
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Comentar',
      category: 'toolbar',
    }
  )

  // T - Attach
  useKeyboardShortcut(
    't',
    () => {
      if (toolbarWaitingForAction && permissions.canAttach && demandData.handleAttach) {
        demandData.handleAttach()
        setToolbarWaitingForAction(false)
      }
    },
    {
      enabled: shortcutsEnabled && toolbarWaitingForAction,
      description: 'Anexar arquivo',
      category: 'toolbar',
    }
  )

  return (
    <div className='h-[calc(100vh-120px)] flex flex-col overflow-hidden'>
      {/* Floating Toolbar */}
      <DemandToolbar
        // Observador: pode puxar para si se tiver permissão (mesmo sendo apenas observador)
        onAssignToSelf={permissions.canPull ? demandData.handlePullToSelf : undefined}
        onEdit={
          !viewHelpers.isOnlyObserver && permissions.canEdit && onEditToggle
            ? handleStartEdit
            : undefined
        }
        onAssignTo={
          !viewHelpers.isOnlyObserver && permissions.canAssign
            ? () => demandData.setAssignModal({ ...demandData.assignModal, isOpen: true })
            : undefined
        }
        onRemoveAssigned={
          !viewHelpers.isOnlyObserver && permissions.canRemoveAssigned
            ? demandData.handleOpenRemoveAssignedModal
            : undefined
        }
        onDeliver={
          !viewHelpers.isOnlyObserver && permissions.canSolve
            ? () => demandData.setSolveModal({ ...demandData.solveModal, isOpen: true })
            : undefined
        }
        onAccept={
          !viewHelpers.isOnlyObserver && permissions.canAccept ? demandData.handleAccept : undefined
        }
        onUndoDelivery={
          !viewHelpers.isOnlyObserver && permissions.canUndoDelivery
            ? demandData.handleUndoDelivery
            : undefined
        }
        onAcceptDelivery={
          !viewHelpers.isOnlyObserver && permissions.canFinalize
            ? demandData.handleAcceptDelivery
            : undefined
        }
        onReturn={
          !viewHelpers.isOnlyObserver && permissions.canReturn ? demandData.handleReturn : undefined
        }
        onReject={
          !viewHelpers.isOnlyObserver && permissions.canReject ? demandData.handleReject : undefined
        }
        onDelete={
          !viewHelpers.isOnlyObserver && permissions.canDelete ? demandData.handleDelete : undefined
        }
        // onSendReminder={!viewHelpers.isOnlyObserver ? () => {} /* TODO: implementar sendReminder */ : undefined}
        onAddObservers={
          !viewHelpers.isOnlyObserver && permissions.canAddObservers
            ? () =>
                demandData.setAddObserversModal({ ...demandData.addObserversModal, isOpen: true })
            : undefined
        }
        // onTransferSector={!viewHelpers.isOnlyObserver ? () => {} /* TODO: implementar transferSector */ : undefined}
        onWrongDemand={
          !viewHelpers.isOnlyObserver && permissions.canMarkWrong
            ? demandData.handleMarkWrong
            : undefined
        }
        onresend={
          !viewHelpers.isOnlyObserver && permissions.canResend && onEditToggle
            ? handleStartResend
            : undefined
        }
        // Observador pode comentar e anexar
        onComment={permissions.canComment ? demandData.handleComment : undefined}
        onAttach={permissions.canAttach ? demandData.handleAttach : undefined}
        // Demanda relacionada (padrão: ir para criar demanda)
        onRelatedDemand={!viewHelpers.isOnlyObserver ? handleRelatedDemand : undefined}
        // Ações de Autorização - apenas para autorizadores com autorização pendente
        onApprove={autorizacao.canAutorizar ? autorizacao.handleAprovarClick : undefined}
        onReprove={autorizacao.canAutorizar ? autorizacao.handleReprovarClick : undefined}
      />

      {/* Layout Principal - Container Principal */}
      <div className='flex-1 flex flex-col min-h-0 overflow-hidden'>
        {/* Header da Demanda - Título, Status, Prioridade e Datas */}
        <div className='flex-shrink-0 bg-white dark:bg-neutral-800 rounded-2xl shadow-lg border border-gray-200/50 dark:border-neutral-700/50 p-3 lg:p-5 mb-2 lg:mb-4'>
          <div className='flex flex-col space-y-2 lg:space-y-4'>
            {/* Título e Informações Principais */}
            <div className='flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 lg:gap-6'>
              {/* Título e Badges */}
              <div className='flex-1'>
                <div className='flex flex-col gap-2 lg:gap-4'>
                  <h1 className='text-lg lg:text-2xl font-bold text-gray-900 dark:text-white'>
                    #{demanda.id} - {demanda.titulo}
                  </h1>

                  {/* Status e Prioridade como badges */}
                  <div className='flex items-center gap-3 flex-wrap'>
                    <Badge
                      variant={getStatusColor(demanda.status || '')}
                      size='md'
                      icon={React.createElement(getStatusIcon(demanda.status || ''), {
                        className: 'w-4 h-4',
                      })}
                    >
                      {getStatusLabel(demanda.status || '')}
                    </Badge>

                    <Badge
                      variant={getPriorityColor(demanda.prioridade)}
                      size='md'
                      icon={React.createElement(getPriorityIcon(demanda.prioridade), {
                        className: 'w-4 h-4',
                      })}
                    >
                      {demanda.prioridade}
                    </Badge>

                    {/* Badge de Termo de Colaboração SEE */}
                    {demanda.template_respostas?.respostas?.termo_colaboracao_see === true && (
                      <Badge
                        variant='info'
                        size='md'
                      >
                        Termo de Colaboração SEE
                      </Badge>
                    )}

                    {/* Badge de Observador + Botão de Recusar */}
                    {demandData.isCurrentUserObserver && (
                      <div className='flex items-center gap-2'>
                        <Button
                          variant='ghost'
                          size='sm'
                          onClick={demandData.handleRefuseObserver}
                          disabled={demandData.isRefusingObserver}
                          className='h-7 px-2 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:bg-orange-900/20'
                          title='Deixar de observar esta demanda'
                        >
                          <HiOutlineEyeSlash className='w-3.5 h-3.5 mr-1' />
                          {demandData.isRefusingObserver ? 'Recusando...' : 'Deixar de Observar'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Data de Entrega - Destacada */}
              <div className='flex flex-col items-center gap-1'>
                <label className='text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide'>
                  Data de Entrega
                </label>

                {/* Sugestão de Entrega - Destaque principal */}
                <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-5 py-2.5 rounded-lg border border-blue-200/50 dark:border-blue-700/50'>
                  <div className='flex items-center gap-2'>
                    <div className='p-1 bg-blue-100 dark:bg-blue-800 rounded-full'>
                      <HiCalendarDays className='w-3.5 h-3.5 text-blue-600 dark:text-blue-400' />
                    </div>
                    <p className='text-sm font-semibold text-blue-900 dark:text-blue-100'>
                      {formatDateSimple(demanda.sugestao_entrega)}
                    </p>
                  </div>
                </div>

                {/* Previsão de Entrega - Ghost style, menos destacado */}
                {demanda.previsao_entrega && (
                  <div className='text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5'>
                    <span className='opacity-60'>Previsão:</span>
                    <span className='opacity-75 font-medium'>
                      {formatDateSimple(demanda.previsao_entrega)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer - Datas de sistema, botão de editar e ordem de autorização */}
            <div className='flex items-center justify-between pt-2 border-t border-gray-200/50 dark:border-neutral-700/50'>
              <div className='flex items-center justify-between gap-6 w-full'>
                <div className='flex gap-2'>
                  <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                    <FaRegBuilding className='w-4 h-4' />
                    <span>
                      {' '}
                      De {viewHelpers.setorOrigemNome} para{' '}
                      {demanda.setor?.nome_setor || 'Não informado'}
                    </span>
                  </div>

                  <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                    <HiCalendarDays className='w-4 h-4' />
                    <span>
                      Criado dia {formatDateForDisplay(demanda.created_at)} Atualizado em{' '}
                      {formatDateForDisplay(demanda.updated_at)}
                    </span>
                  </div>
                </div>
                <div className='flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400'>
                  {/*Autorizações - só aparece se houver autorizações */}
                  {autorizacoesData?.autorizacoes && autorizacoesData.autorizacoes.length > 0 && (
                    <div className='flex-1 lg:flex-[1] flex flex-col min-h-0'>
                      <AutorizacaoTimeline
                        autorizacoes={autorizacoesData.autorizacoes}
                        isSequencial={autorizacoesData.is_sequencial}
                        className='flex-1'
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Botões de Ação - Apenas modo edição e não em fluxo de reenvio */}
              {isEditing && !isResendFlow && (
                <div className='flex items-center gap-3'>
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={() => {
                      setIsResendFlow(false)
                      setResendComment('')
                      demandData.handleCancel()
                    }}
                    disabled={demandData.isSaving}
                    className={
                      demandData.hasUnsavedChanges
                        ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'
                        : ''
                    }
                  >
                    <HiXMark className='w-4 h-4 mr-1' />
                    Cancelar
                    {demandData.hasUnsavedChanges && (
                      <span
                        className='ml-1 w-2 h-2 bg-yellow-500 rounded-full animate-pulse'
                        title='Há alterações não salvas'
                      />
                    )}
                  </Button>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => {
                      setIsResendFlow(false)
                      setResendComment('')
                      demandData.handleSave()
                    }}
                    disabled={demandData.isSaving || !demandData.hasUnsavedChanges}
                    className={
                      demandData.hasUnsavedChanges ? 'bg-green-600 hover:bg-green-700' : ''
                    }
                  >
                    <HiCheck className='w-4 h-4 mr-1' />
                    {demandData.isSaving
                      ? 'Salvando...'
                      : demandData.hasUnsavedChanges
                        ? 'Salvar Alterações'
                        : 'Salvar'}
                    {demandData.hasUnsavedChanges && (
                      <span
                        className='ml-1 w-2 h-2 bg-green-200 rounded-full animate-pulse'
                        title='Há alterações para salvar'
                      />
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Barra de Informações - Sempre no topo */}
        <InfoDemands demanda={demanda} />

        <div className='flex-1 flex flex-col lg:flex-row gap-4 min-h-0'>
          {/* Primeira Coluna - Descrição */}
          <div className='flex-1 lg:flex-[2] flex flex-col min-h-0 overflow-hidden'>
            {/* Mensagem de erro se houver */}
            {demandData.saveError && (
              <Card className='p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 mb-4 flex-shrink-0'>
                <div className='flex items-center gap-3'>
                  <HiXMark className='w-4 h-4 text-red-600 dark:text-red-400' />
                  <span className='text-sm text-red-600 dark:text-red-400'>
                    {demandData.saveError}
                  </span>
                </div>
              </Card>
            )}

            {/* Card de Descrição e Anexos */}
            <Card className='flex-1 flex flex-col min-h-0 overflow-hidden rounded-xl'>
              {/* Header fixo */}
              <div className='flex-shrink-0 p-5 border-b border-gray-200 dark:border-neutral-700'>
                <div className='flex items-center gap-3'>
                  <div className='p-2 bg-gray-100 dark:bg-gray-700 rounded-lg'>
                    <HiDocumentText className='w-5 h-5 text-gray-600 dark:text-gray-400' />
                  </div>
                  <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                    {demanda.template_respostas ? 'Dados do Formulário' : 'Descrição'}
                  </h3>
                </div>
              </div>

              {/* Descrição/Template - Área principal com scroll */}
              <div className='flex-1 overflow-y-auto min-h-0 p-5'>
                {/* Se tem template_respostas, mostrar APENAS o template (não a descrição duplicada) */}
                {demanda.template_respostas ? (
                  <TemplateFieldsViewer
                    templateRespostas={isEditing && demandData.editData.template_respostas ? demandData.editData.template_respostas : demanda.template_respostas}
                    allowInlineEdit={!isEditing && permissions.canEditTemplateFields}
                    isEditing={isEditing}
                    showHeader={false}
                    onFieldChange={(fieldName, value) => {
                      // No modo de edição completa, atualizar editData
                      demandData.setEditData(prev => ({
                        ...prev,
                        template_respostas: {
                          ...(prev.template_respostas || demanda.template_respostas!),
                          respostas: {
                            ...(prev.template_respostas?.respostas || demanda.template_respostas!.respostas),
                            [fieldName]: value,
                          },
                        },
                      }))
                    }}
                    onSaveField={async (fieldName, value) => {
                      // Atualizar o campo no template_respostas (edição inline)
                      const novasRespostas = {
                        ...demanda.template_respostas!.respostas,
                        [fieldName]: value,
                      }

                      if (onSave) {
                        await onSave({
                          template_respostas: {
                            ...demanda.template_respostas!,
                            respostas: novasRespostas,
                          },
                        })
                      }
                    }}
                  />
                ) : // Se não tem template, mostrar descrição normal
                isEditing ? (
                  <Textarea
                    variant='rich'
                    value={demandData.editData.descricao}
                    onChange={value =>
                      demandData.setEditData(prev => ({ ...prev, descricao: value }))
                    }
                    placeholder='Descreva detalhadamente a demanda...'
                    className='w-full min-h-[300px]'
                  />
                ) : (
                  <RichTextDisplay content={demanda.descricao} className='leading-relaxed' />
                )}
              </div>

              {/* Anexos - Área colapsável */}
              {demanda.anexos && demanda.anexos.length > 0 && (
                <div className='flex-shrink-0 border-t border-gray-200 dark:border-neutral-700'>
                  {/* Header - Sempre Visível */}
                  <button
                    data-attachments-toggle
                    onClick={() => setIsAttachmentsExpanded(!isAttachmentsExpanded)}
                    className={`w-full px-5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-all duration-300 group ${
                      isAttachmentsExpanded ? 'py-3' : 'py-2'
                    }`}
                  >
                    <div className='flex items-center gap-3 flex-1 min-w-0'>
                      <div className={`transition-all duration-300 ease-in-out`}>
                        {isAttachmentsExpanded ? (
                          <HiChevronUp className='w-5 h-5 text-blue-600 dark:text-blue-400 transition-transform' />
                        ) : (
                          <HiChevronDown className='w-5 h-5 text-blue-600 dark:text-blue-400 transition-transform' />
                        )}
                      </div>
                      {!isAttachmentsExpanded ? (
                        <div className='flex items-center gap-3 text-sm text-gray-700 dark:text-gray-300 min-w-0 flex-1'>
                          <HiPaperClip className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                          <span className='font-semibold'>Anexos ({demanda.anexos.length})</span>
                          {/* Preview da primeira linha de arquivos */}
                          <div className='flex items-center gap-2 flex-1 min-w-0 overflow-hidden'>
                            {demanda.anexos.slice(0, 5).map((anexo, index) => {
                              const fileName = anexo.nome_anexo || `Anexo ${index + 1}`
                              const fileIcon = getFileIcon(fileName)
                              const isRemoved = !anexo.active

                              return (
                                <div
                                  key={anexo.id || index}
                                  className={`flex items-center gap-1 px-2 py-1 rounded border text-xs ${
                                    isRemoved
                                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 opacity-60'
                                      : 'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-600'
                                  }`}
                                >
                                  <span className='text-base'>{fileIcon}</span>
                                  <span className='truncate max-w-[80px]' title={fileName}>
                                    {fileName}
                                  </span>
                                </div>
                              )
                            })}
                            {demanda.anexos.length > 5 && (
                              <span className='text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap'>
                                +{demanda.anexos.length - 5} mais
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <h3 className='text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2'>
                          <HiPaperClip className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                          ANEXOS ({demanda.anexos.length})
                        </h3>
                      )}
                    </div>
                  </button>

                  {/* Conteúdo Expansível */}
                  {isAttachmentsExpanded && (
                    <div
                      className='px-5 pb-5 max-h-[400px] overflow-y-auto'
                      style={{
                        animation: 'slideInFromTop 0.3s ease-out',
                      }}
                    >
                      <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4'>
                        {demanda.anexos.map((anexo, index) => {
                          const fileName = anexo.nome_anexo || `Anexo ${index + 1}`
                          const fileIcon = getFileIcon(fileName)
                          // NORMALIZAÇÃO: Converte 0/1 para false/true usando Boolean()
                          // eslint-disable-next-line no-extra-boolean-cast
                          const isRemoved = !Boolean(anexo.active)

                          return (
                            <div
                              key={anexo.id || index}
                              className={`group relative border rounded-lg p-3 cursor-pointer transition-all ${
                                isRemoved
                                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 opacity-60'
                                  : 'bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700 hover:shadow-md hover:border-gray-300 dark:hover:border-neutral-600'
                              }`}
                              onClick={() => !isRemoved && demandData.handleViewAttachment(anexo)}
                            >
                              {/* Ícone do arquivo */}
                              <div className='flex flex-col items-center space-y-2'>
                                <div className='text-3xl flex-shrink-0'>{fileIcon}</div>

                                {/* Info do arquivo */}
                                <div className='text-center w-full'>
                                  <p
                                    className={`text-xs font-medium truncate px-1 ${
                                      isRemoved
                                        ? 'text-red-600 dark:text-red-400 line-through'
                                        : 'text-gray-900 dark:text-gray-100'
                                    }`}
                                    title={fileName}
                                  >
                                    {fileName}
                                  </p>
                                </div>
                              </div>

                              {/* Botões de ação (aparecem no hover) */}
                              {!isRemoved && (
                                <div className='absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col space-y-1'>
                                  <button
                                    onClick={e => {
                                      e.stopPropagation()
                                      demandData.handleDownloadAttachment(anexo)
                                    }}
                                    className='p-1 bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-full transition-colors shadow-md'
                                    title='Download'
                                  >
                                    <HiArrowDownTray className='w-3 h-3' />
                                  </button>

                                  {/* Botão de excluir apenas para criador da demanda ou usuário que fez upload */}
                                  {viewHelpers.canDeleteAttachment(anexo) && (
                                    <button
                                      onClick={e => {
                                        e.stopPropagation()
                                        demandData.handleDeleteAttachment(anexo)
                                      }}
                                      className='p-1 bg-red-500 dark:bg-red-600 hover:bg-red-600 dark:hover:bg-red-700 text-white rounded-full transition-colors shadow-md'
                                      title='Remover'
                                    >
                                      <HiTrash className='w-3 h-3' />
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Data de Entrega - Só aparece no modo edição */}
              {isEditing && (
                <div className='flex-shrink-0 border-t border-gray-200 dark:border-neutral-700 p-5'>
                  <div className='flex items-center gap-3 mb-4'>
                    <HiCalendarDays className='w-5 h-5 text-blue-600 dark:text-blue-400' />
                    <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>
                      Data de Entrega
                    </h4>
                  </div>
                  <Input
                    type='date'
                    value={formatDateForInput(demandData.editData.sugestao_entrega)}
                    onChange={e =>
                      demandData.setEditData(prev => ({
                        ...prev,
                        sugestao_entrega: e.target.value,
                      }))
                    }
                    className='w-full max-w-xs'
                  />
                </div>
              )}
            </Card>
          </div>

          {/* Segunda Coluna - Histórico de Atividades (sempre visível) */}
          <div className='flex-1 lg:flex-[1] flex flex-col min-h-0'>
            {/* Histórico de Atividades */}
            <Card className='flex-1 flex flex-col p-0 min-h-0 rounded-xl'>
              {/* Header do Histórico */}
              <div className='flex-shrink-0 p-5'>
                <div className='flex items-center justify-between mb-4'>
                  <div className='flex items-center gap-3'>
                    <div className='p-2 bg-purple-100 dark:bg-purple-700 rounded-lg'>
                      <HiClock className='w-5 h-5 text-purple-600 dark:text-purple-400' />
                    </div>
                    <div>
                      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>
                        Histórico de Atividades
                      </h3>
                      <p className='text-sm text-gray-500 dark:text-gray-400'>
                        Todas as ações realizadas nesta demanda
                      </p>
                    </div>
                  </div>

                  {/* Botão de Impressão */}
                  {demandData.historico.length > 0 && (
                    <SimpleButton
                      variant='ghost'
                      size='sm'
                      onClick={handlePrintHistory}
                      className='flex items-center gap-2 hover:bg-purple-50 dark:hover:bg-purple-900/20 dark:border-purple-700'
                      title='Imprimir histórico'
                    >
                      <HiPrinter className='size-6 text-purple-800 dark:text-purple-400' />
                    </SimpleButton>
                  )}
                </div>
              </div>

              {/* Timeline com scroll */}
              <div className='flex-1 overflow-y-auto min-h-0'>
                <div className='px-5 pb-6'>
                  {demandData.historicoError ? (
                    <div className='text-red-600 dark:text-red-400 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded-lg'>
                      Erro ao carregar histórico: {demandData.historicoError}
                    </div>
                  ) : demandData.historicoLoading ? (
                    <div className='flex items-center justify-center p-8'>
                      <div className='animate-spin w-6 h-6 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full'></div>
                      <span className='ml-2 text-gray-600 dark:text-gray-400'>
                        Carregando histórico...
                      </span>
                    </div>
                  ) : demandData.historico.length > 0 ? (
                    <HistoryTimeline items={demandData.historico} className='pb-4' />
                  ) : (
                    <div className='text-gray-500 dark:text-gray-400 text-sm p-4 text-center'>
                      Nenhum histórico encontrado para esta demanda.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {isEditing && isResendFlow && (
        <div className='fixed -translate-y-18 bottom-6 right-6 z-40 w-[360px] rounded-2xl border border-cyan-200/60 bg-white/95 p-4 shadow-xl backdrop-blur dark:border-cyan-800/50 dark:bg-neutral-900/90'>
          <div className='flex items-center justify-between mb-3'>
            <div className='flex items-center gap-2'>
              <div className='p-1.5 rounded-full bg-cyan-100 dark:bg-cyan-900/40'>
                <HiArrowUpTray className='w-4 h-4 text-cyan-700 dark:text-cyan-300' />
              </div>
              <span className='text-sm font-semibold text-gray-900 dark:text-gray-100'>
                Reenviar demanda
              </span>
            </div>
            <button
              type='button'
              onClick={handleCancelResend}
              className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            >
              Fechar
            </button>
          </div>
          <div className='mb-3 p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200/50 dark:border-cyan-800/30'>
            <p className='text-xs text-cyan-800 dark:text-cyan-300'>
              As alterações nos campos serão salvas e reenviadas com o comentário abaixo.
            </p>
          </div>
          <Textarea
            variant='rich'
            value={resendComment}
            onChange={setResendComment}
            placeholder='Comentário opcional para o reenvio...'
            className='w-full'
            rows={3}
            disabled={demandData.remandModal.isLoading}
          />
          <div className='flex gap-2 mt-3'>
            <Button
              variant='outline'
              onClick={handleCancelResendAndEdit}
              disabled={demandData.remandModal.isLoading}
              className='flex-1'
            >
              Cancelar tudo
            </Button>
            <Button
              variant='primary'
              onClick={handleResendDemand}
              disabled={demandData.remandModal.isLoading}
              isLoading={demandData.remandModal.isLoading}
              className='flex-1 bg-cyan-600 hover:bg-cyan-700'
            >
              {demandData.remandModal.isLoading ? 'Reenviando...' : 'Reenviar'}
            </Button>
          </div>
        </div>
      )}

      {/* Modais */}
      <AlertModal
        isOpen={demandData.alertModal.isOpen}
        type={demandData.alertModal.type}
        {...(demandData.alertModal.title ? { title: demandData.alertModal.title } : {})}
        message={demandData.alertModal.message}
        onClose={() => demandData.setAlertModal({ ...demandData.alertModal, isOpen: false })}
      />

      <ConfirmModal
        isOpen={demandData.confirmModal.isOpen}
        type={demandData.confirmModal.type}
        title={demandData.confirmModal.title || 'Confirmação'}
        message={demandData.confirmModal.message}
        {...(demandData.confirmModal.details ? { details: demandData.confirmModal.details } : {})}
        onConfirm={demandData.confirmModal.onConfirm || (() => {})}
        onClose={() => demandData.setConfirmModal({ ...demandData.confirmModal, isOpen: false })}
      />

      <NavigationUnsavedChangesModal
        isOpen={demandData.navigationModal.isOpen}
        onConfirm={demandData.navigationModal.onConfirm || (() => {})}
        onClose={() => demandData.setNavigationModal({ isOpen: false })}
      />

      <SolveDemandModal
        isOpen={demandData.solveModal.isOpen}
        isMinimized={demandData.solveModal.isMinimized}
        onMinimize={() => demandData.setSolveModal({ ...demandData.solveModal, isMinimized: true })}
        onRestore={() => demandData.setSolveModal({ ...demandData.solveModal, isMinimized: false })}
        onClose={() => demandData.setSolveModal({ isOpen: false, isMinimized: false })}
        onConfirm={demandData.handleSolveDemand}
        loading={demandData.solveModal.loading || false}
      />

      <FinalizeDemandModal
        isOpen={demandData.finalizeModal.isOpen}
        isMinimized={demandData.finalizeModal.isMinimized}
        onMinimize={() =>
          demandData.setFinalizeModal({ ...demandData.finalizeModal, isMinimized: true })
        }
        onRestore={() =>
          demandData.setFinalizeModal({ ...demandData.finalizeModal, isMinimized: false })
        }
        onClose={demandData.handleCloseFinalizeModal}
        onConfirm={demandData.handleFinalizeDemand}
        loading={demandData.finalizeModal.isLoading || false}
      />

      <AssignDemandModal
        isOpen={demandData.assignModal.isOpen}
        isMinimized={demandData.assignModal.isMinimized}
        isLoading={demandData.assignModal.isLoading}
        selectedUserId={demandData.selectedUserId}
        assignComment={demandData.assignComment}
        userOptions={demandData.userOptions}
        onMinimize={() =>
          demandData.setAssignModal({ ...demandData.assignModal, isMinimized: true })
        }
        onRestore={() =>
          demandData.setAssignModal({ ...demandData.assignModal, isMinimized: false })
        }
        onClose={demandData.handleCloseAssignModal}
        onUserChange={demandData.setSelectedUserId}
        onCommentChange={demandData.setAssignComment}
        onConfirm={demandData.handleAssignUser}
      />

      <AttachFilesModal
        isOpen={demandData.commentAttachModal.isOpen}
        isMinimized={demandData.commentAttachModal.isMinimized}
        isLoading={demandData.commentAttachModal.isLoading}
        attachments={demandData.attachments}
        attachmentComments={demandData.attachmentComments}
        comment={demandData.comment}
        isDragOver={demandData.isDragOver}
        onMinimize={() =>
          demandData.setCommentAttachModal({ ...demandData.commentAttachModal, isMinimized: true })
        }
        onRestore={() =>
          demandData.setCommentAttachModal({ ...demandData.commentAttachModal, isMinimized: false })
        }
        onClose={demandData.handleCloseCommentAttachModal}
        onConfirm={(comment, attachments, attachmentComments) => {
          // Atualizar os estados primeiro
          demandData.setComment(comment)
          demandData.setAttachments(attachments)
          demandData.setAttachmentComments(attachmentComments)
          // Depois chamar a função de submit
          demandData.handleSubmitCommentAttach()
        }}
        onCommentChange={demandData.setComment}
        onAttachmentCommentChange={(fileKey: string, comment: string) => {
          demandData.setAttachmentComments(prev => ({ ...prev, [fileKey]: comment }))
        }}
        onAddFiles={demandData.handleAddFiles}
        onRemoveFile={demandData.handleRemoveFile}
        onDragEnter={demandData.handleDragEnter}
        onDragLeave={demandData.handleDragLeave}
        onDragOver={demandData.handleDragOver}
        onDrop={demandData.handleDrop}
      />

      <ManageObserversModal
        isOpen={demandData.addObserversModal.isOpen}
        isMinimized={demandData.addObserversModal.isMinimized}
        isLoading={demandData.addObserversModal.isLoading}
        selectedObservers={demandData.selectedObservers}
        observersToRemove={demandData.observersToRemove || []}
        observersComment={demandData.observersComment}
        userOptions={demandData.userOptions}
        currentObservers={mapObserversForModal(demanda.observadores)}
        onMinimize={() =>
          demandData.setAddObserversModal({ ...demandData.addObserversModal, isMinimized: true })
        }
        onRestore={() =>
          demandData.setAddObserversModal({ ...demandData.addObserversModal, isMinimized: false })
        }
        onClose={demandData.handleCloseAddObserversModal}
        onObserversChange={demandData.setSelectedObservers}
        onCommentChange={demandData.setObserversComment}
        onToggleObserverRemoval={demandData.handleToggleObserverRemoval}
        onConfirm={demandData.handleManageObservers}
      />

      {/* PromptModal */}
      <PromptModal
        isOpen={demandData.promptModal.isOpen}
        isMinimized={demandData.promptModal.isMinimized}
        loading={demandData.promptModal.isLoading || false}
        onMinimize={() => demandData.setPromptModal(prev => ({ ...prev, isMinimized: true }))}
        onRestore={demandData.restorePromptModal}
        {...(demandData.promptModal.title ? { title: demandData.promptModal.title } : {})}
        message={demandData.promptModal.message}
        {...(demandData.promptModal.placeholder
          ? { placeholder: demandData.promptModal.placeholder }
          : {})}
        {...(demandData.promptModal.defaultValue
          ? { defaultValue: demandData.promptModal.defaultValue }
          : {})}
        {...(demandData.promptModal.multiline !== undefined
          ? { multiline: demandData.promptModal.multiline }
          : {})}
        {...(demandData.promptModal.richText !== undefined
          ? { richText: demandData.promptModal.richText }
          : {})}
        onClose={() =>
          demandData.setPromptModal({
            isOpen: false,
            isMinimized: false,
            isLoading: false,
            message: '',
          })
        }
        onConfirm={demandData.promptModal.onConfirm || (() => {})}
        // Props para minimização
        modalId='demand-prompt-modal'
        toolbarActionId='prompt-comment'
      />

      {/* Modal de Remoção de Atribuídos */}
      <RemoveAssignedModal
        isOpen={demandData.removeAssignedModal.isOpen}
        isMinimized={demandData.removeAssignedModal.isMinimized}
        isLoading={demandData.removeAssignedModal.isLoading}
        assignedUsers={mapAssignedForModal(demanda.atribuidas)}
        usersToRemove={demandData.usersToRemove || []}
        onMinimize={() =>
          demandData.setRemoveAssignedModal({
            ...demandData.removeAssignedModal,
            isMinimized: true,
          })
        }
        onRestore={() =>
          demandData.setRemoveAssignedModal({
            ...demandData.removeAssignedModal,
            isMinimized: false,
          })
        }
        onClose={demandData.handleCloseRemoveAssignedModal}
        onToggleUserRemoval={demandData.handleToggleUserRemoval}
        onConfirm={demandData.handleBatchRemoveAssigned}
      />

      {/* Modal de Autorização */}
      <AutorizacaoModal
        isOpen={autorizacao.showAutorizacaoModal}
        onClose={autorizacao.handleCancelAutorizacao}
        onConfirm={autorizacao.handleConfirmAutorizacao}
        action={autorizacao.autorizacaoAction}
        isProcessing={autorizacao.isProcessing}
        motivo={autorizacao.comentario}
        onMotivoChange={autorizacao.setComentario}
      />
    </div>
  )
}
